import {ICustomTableColumn} from '~/components/CustomTable';
import FinancialRecordActionHandler from './FinancialRecordActionHandler';
import './styles.scss';
import {getPaymentStatusDisplay} from '../../utils';

import {FinanceRecordType, FinancialRecordData} from './types';

export type FinancialRecord = {
  id: number;
  invoiceNumber: string;
  description: string;
  billingDate: string;
  amount: number;
  paymentMethodType: string;
  status: string;
};

export const getFinancialRecordTableColumns = (
  recordType: FinanceRecordType
): ICustomTableColumn<FinancialRecordData>[] => [
  {
    key: 'invoiceNumber',
    label: 'Invoice Number',
    isBold: true,
    align: 'center',
  },
  {
    key: 'description',
    label: 'Description',
    align: 'center',
  },
  {
    key: 'billingDate',
    label: 'Date & Time',
    align: 'center',
  },
  {
    key: 'amount',
    label: 'Cost(£)',
    align: 'center',
  },
  {
    key: 'paymentMethodType',
    label: 'Method',
    align: 'center',
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
  },
  {
    key: 'actions',
    label: 'Actions',
    align: 'center',
    render: (_: unknown, row: FinancialRecordData) => (
      <FinancialRecordActionHandler rowData={row} recordType={recordType} />
    ),
  },
];

const getStatusClassName = (displayStatus: string): string => {
  switch (displayStatus) {
    case 'Paid':
      return 'status-paid';
    case 'Unpaid':
      return 'status-unpaid';
    case 'Pending':
      return 'status-pending';
    case 'Refunded':
      return 'status-refunded';
    case 'Refund Requested':
      return 'status-refund-requested';
    case 'Outstanding':
      return 'status-outstanding';
    case 'New Claim':
      return 'status-new_claim';
    case 'Submitted':
      return 'status-submitted';
    case 'Rejected':
      return 'status-rejected';
    default:
      return 'status-default';
  }
};

export const renderStatusColumn = (_: unknown, row: FinancialRecordData) => {
  const status = row.status;
  const displayText = getPaymentStatusDisplay(status);
  const statusClass = getStatusClassName(displayText);

  return <span className={`status-badge ${statusClass}`}>{displayText}</span>;
};

const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || value === '-';

export const renderColumnWithDash = (
  _: unknown,
  row: FinancialRecordData,
  key: keyof FinancialRecord
) => {
  const value = (row as FinancialRecord)[key];
  return isEmpty(value) ? (
    <span className="dash-value">-</span>
  ) : (
    <span>{value}</span>
  );
};

export const renderAmountColumn = (_: unknown, row: FinancialRecordData) => {
  const value = (row as FinancialRecord).amount;

  if (value === null || value === undefined || isNaN(Number(value))) {
    return <span className="dash-value">-</span>;
  }

  return <span>{Number(value).toFixed(2)}</span>;
};

export const getModifiedColumns = (
  recordType: FinanceRecordType = 'payment'
): ICustomTableColumn<FinancialRecordData>[] => {
  return getFinancialRecordTableColumns(recordType).map(col => {
    switch (col.key) {
      case 'status':
        return {
          ...col,
          render: renderStatusColumn,
        };
      case 'amount':
        return {
          ...col,
          render: renderAmountColumn,
        };
      case 'description':
        return {
          ...col,
          render: (_: unknown, row: FinancialRecordData) =>
            renderColumnWithDash(_, row, col.key as keyof FinancialRecord),
        };
      case 'paymentMethodType':
        return {
          ...col,
          render: (_: unknown, row: FinancialRecordData) =>
            renderColumnWithDash(_, row, col.key as keyof FinancialRecord),
        };
      case 'billingDate':
        return {
          ...col,
          render: (_: unknown, row: FinancialRecordData) =>
            renderColumnWithDash(_, row, col.key as keyof FinancialRecord),
        };
      case 'invoiceNumber':
        return {
          ...col,
          render: (_: unknown, row: FinancialRecordData) =>
            renderColumnWithDash(_, row, col.key as keyof FinancialRecord),
        };
      case 'actions':
        return {
          ...col,
          render: (_: unknown, row: FinancialRecordData) => (
            <FinancialRecordActionHandler
              rowData={row}
              recordType={recordType}
            />
          ),
        };
      default:
        return col;
    }
  });
};
